import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import BackBar from '../components/BackBar';

const API = process.env.REACT_APP_BACKEND_URL;

const getAuthHeader = () => {
  const token = localStorage.getItem('opwmp_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Parameter labels for display
const PARAMETER_LABELS = [
  { key: 'residential_waste_streams', label: 'Residential Waste Streams', group: 'Waste Streams' },
  { key: 'commercial_waste_streams', label: 'Commercial Waste Streams', group: 'Waste Streams' },
  { key: 'generation_rates_basis', label: 'Generation Rates Basis', group: 'Rates' },
  { key: 'collection_frequency', label: 'Collection Frequency', group: 'Collection' },
  { key: 'collection_method', label: 'Collection Method', group: 'Collection' },
  { key: 'bulky_waste_sizing_residential', label: 'Bulky Waste Sizing (Residential)', group: 'Bulky Waste' },
  { key: 'bulky_waste_sizing_commercial', label: 'Bulky Waste Sizing (Commercial)', group: 'Bulky Waste' },
  { key: 'chute_allowances', label: 'Chute Allowances', group: 'Chute' },
  { key: 'chute_streams', label: 'Chute Streams', group: 'Chute' },
  { key: 'chute_height_threshold', label: 'Chute Height Threshold', group: 'Chute' },
  { key: 'accepted_bin_sizes', label: 'Accepted Bin Sizes', group: 'Bins' },
  { key: 'kerbside_requirements', label: 'Kerbside Requirements', group: 'Collection' },
  { key: 'manual_transport_distance', label: 'Manual Transport Distance', group: 'Transport' },
  { key: 'storage_to_kerbside_distance', label: 'Storage to Kerbside Distance', group: 'Transport' },
  { key: 'slope_grade_threshold', label: 'Slope Grade Threshold', group: 'Transport' },
  { key: 'interim_storage_rules', label: 'Interim Storage Rules', group: 'Storage' },
  { key: 'fogo_mandates', label: 'FOGO 2030 Mandates', group: 'Compliance' },
  { key: 'dcp_reference', label: 'DCP Reference Document', group: 'Reference' },
  { key: 'private_contractor_allowed', label: 'Private Contractor Allowed', group: 'Collection' },
];

const AdminNSWCouncils = () => {
  const navigate = useNavigate();
  const [councils, setCouncils] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCouncil, setSelectedCouncil] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'table'
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const fetchCouncils = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/admin/nsw-councils`, {
        headers: getAuthHeader()
      });
      setCouncils(response.data);
    } catch (err) {
      console.error('Failed to fetch councils:', err);
      if (err.response?.status === 403) {
        navigate('/dashboard');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchCouncils();
  }, [fetchCouncils]);

  const filteredCouncils = councils.filter(c =>
    c.council_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleEditCouncil = (council) => {
    setEditData({ ...council });
    setSelectedCouncil(council);
    setShowEditModal(true);
    setError('');
    setSuccessMsg('');
  };

  const handleSaveCouncil = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccessMsg('');

    try {
      await axios.post(`${API}/api/admin/nsw-councils`, editData, {
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' }
      });
      setSuccessMsg('Council data saved successfully');
      setShowEditModal(false);
      fetchCouncils();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save council data');
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadPDF = async (councilName) => {
    try {
      const encodedName = encodeURIComponent(councilName);
      const response = await axios.get(
        `${API}/api/admin/nsw-councils/${encodedName}/pdf`,
        {
          headers: getAuthHeader(),
          responseType: 'blob'
        }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${councilName.replace(/\s/g, '_')}_DCP_Reference.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('PDF download failed:', err);
      setError('Failed to download PDF');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleExportCSV = async () => {
    try {
      const response = await axios.get(`${API}/api/admin/nsw-councils-export-csv`, {
        headers: getAuthHeader(),
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'nsw_councils_data.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Failed to export CSV');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleImportCSV = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setImporting(true);
    setImportResult(null);
    setError('');
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API}/api/admin/nsw-councils-import-csv`, formData, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
      });
      setImportResult(response.data);
      fetchCouncils();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to import CSV');
    } finally {
      setImporting(false);
      e.target.value = '';
    }
  };

  const getDataCompleteness = (council) => {
    if (!council.has_data) return 0;
    let filled = 0;
    PARAMETER_LABELS.forEach(p => {
      if (council[p.key] && council[p.key] !== 'TBC') filled++;
    });
    return Math.round((filled / PARAMETER_LABELS.length) * 100);
  };

  const getCompletenessColor = (pct) => {
    if (pct === 0) return 'bg-gray-200';
    if (pct < 30) return 'bg-red-400';
    if (pct < 60) return 'bg-amber-400';
    if (pct < 90) return 'bg-blue-400';
    return 'bg-green-500';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-gray-500" data-testid="nsw-councils-loading">Loading councils...</div>
      </div>
    );
  }

  const dataFilledCount = councils.filter(c => c.has_data).length;

  return (
    <div className="min-h-screen bg-gray-100" data-testid="nsw-councils-page">
      <BackBar to="/admin" label="Admin" />
      {/* Header */}
      <header className="bg-gray-900 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-xl font-bold">NSW Council Reference Tool</h1>
              <p className="text-gray-400 text-sm">DCP waste management parameters for 33 metro councils</p>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-400">
                {dataFilledCount}/{councils.length} councils with data
              </span>
              <button
                onClick={handleExportCSV}
                className="text-sm text-gray-300 hover:text-white px-3 py-1.5 rounded border border-gray-600 hover:border-gray-400"
                data-testid="export-csv-btn"
              >
                Export CSV
              </button>
              <label className="text-sm text-gray-300 hover:text-white px-3 py-1.5 rounded border border-gray-600 hover:border-gray-400 cursor-pointer">
                {importing ? 'Importing...' : 'Import CSV'}
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleImportCSV}
                  className="hidden"
                  disabled={importing}
                  data-testid="import-csv-input"
                />
              </label>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search & View Toggle */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="flex-1 relative">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              placeholder="Search councils..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-sm"
              data-testid="council-search-input"
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setViewMode('grid')}
              className={`px-3 py-2 rounded-lg text-sm font-medium ${viewMode === 'grid' ? 'bg-amber-600 text-white' : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'}`}
              data-testid="view-grid-btn"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
              </svg>
            </button>
            <button
              onClick={() => setViewMode('table')}
              className={`px-3 py-2 rounded-lg text-sm font-medium ${viewMode === 'table' ? 'bg-amber-600 text-white' : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'}`}
              data-testid="view-table-btn"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
              </svg>
            </button>
          </div>
        </div>

        {/* Error / Success Messages */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm" data-testid="error-message">
            {error}
          </div>
        )}
        {successMsg && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm" data-testid="success-message">
            {successMsg}
          </div>
        )}
        {importResult && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 text-blue-700 rounded-lg text-sm" data-testid="import-result">
            <p className="font-medium">{importResult.message}</p>
            {importResult.errors?.length > 0 && (
              <ul className="mt-1 text-xs text-red-600">
                {importResult.errors.map((err, i) => <li key={i}>{err}</li>)}
              </ul>
            )}
          </div>
        )}

        {/* Grid View */}
        {viewMode === 'grid' && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="councils-grid">
            {filteredCouncils.map((council) => {
              const completeness = getDataCompleteness(council);
              const freq = (council.collection_frequency || '').toLowerCase();
              const streams = council.residential_waste_streams || '';
              const rates = council.generation_rates_basis || '';
              const chute = council.chute_allowances || '';
              const chuteStreams = council.chute_streams || '';

              // Parse collection frequency per stream - split by ; to avoid cross-segment matching
              const parseFreq = (text, streamText) => {
                const t = text.toLowerCase();
                const st = (streamText || '').toLowerCase();
                const hasSepPC = st.includes('blue lid') || st.includes('paper & cardboard (blue') || st.includes('paper/cardboard (blue') || st.includes('paper & cardboard (yellow lid') || (st.includes('container recycling') && st.includes('paper & cardboard'));
                const result = [];
                const segments = t.split(';').map(s => s.trim());
                // GW
                const gwSeg = segments.find(s => s.includes('general waste') || s.includes(' gw') || s.includes('residual') || s.includes('garbage'));
                if (gwSeg) {
                  if (gwSeg.includes('twice') || gwSeg.includes('2x')) result.push({ stream: 'GW', freq: '2x/wk', color: 'bg-purple-100 text-purple-800' });
                  else if (gwSeg.includes('fortnightly')) result.push({ stream: 'GW', freq: 'F', color: 'bg-amber-100 text-amber-800' });
                  else if (gwSeg.includes('weekly')) result.push({ stream: 'GW', freq: 'W', color: 'bg-green-100 text-green-800' });
                }
                // Recycling
                const recySeg = segments.find(s => s.includes('recycling') || s.includes('commingled') || s.includes('container recycl'));
                if (recySeg) {
                  if (recySeg.includes('fortnightly')) result.push({ stream: hasSepPC ? 'Cont.' : 'Recy', freq: 'F', color: 'bg-amber-100 text-amber-800' });
                  else if (recySeg.includes('weekly')) result.push({ stream: hasSepPC ? 'Cont.' : 'Recy', freq: 'W', color: 'bg-green-100 text-green-800' });
                }
                // FOGO/Organics/Garden - use first frequency keyword position
                const fogoSeg = segments.find(s => s.includes('fogo') || s.includes('organics') || s.includes('garden') || s.includes('vegetation'));
                if (fogoSeg) {
                  const fi = fogoSeg.indexOf('fortnightly');
                  const wi = fogoSeg.indexOf('weekly');
                  const ti = fogoSeg.indexOf('twice');
                  const positions = [
                    fi >= 0 ? { pos: fi, freq: 'F', color: 'bg-amber-100 text-amber-800' } : null,
                    ti >= 0 ? { pos: ti, freq: '2x/wk', color: 'bg-purple-100 text-purple-800' } : null,
                    wi >= 0 && (ti < 0 || wi < ti) ? { pos: wi, freq: 'W', color: 'bg-green-100 text-green-800' } : null,
                  ].filter(Boolean);
                  if (positions.length > 0) {
                    const first = positions.sort((a, b) => a.pos - b.pos)[0];
                    result.push({ stream: 'FOGO', freq: first.freq, color: first.color });
                  }
                }
                // P&C (separate stream)
                if (hasSepPC) {
                  const pcSeg = segments.find(s => s.includes('paper') || s.includes('p&c') || s.includes('cardboard'));
                  if (pcSeg) {
                    if (pcSeg.includes('fortnightly')) result.push({ stream: 'P&C', freq: 'F', color: 'bg-amber-100 text-amber-800' });
                    else if (pcSeg.includes('weekly')) result.push({ stream: 'P&C', freq: 'W', color: 'bg-green-100 text-green-800' });
                  }
                }
                return result;
              };

              // Parse waste streams into badges
              const parseStreams = (text) => {
                const items = [];
                const t = text.toLowerCase();
                // GW always present
                if (t.includes('general waste') || t.includes('residual')) items.push({ name: 'GW', lid: 'red', color: 'bg-red-100 text-red-700 border-red-200' });
                // Detect if P&C is a SEPARATE stream (blue lid, or explicitly listed as own bin)
                const hasSeparatePC = t.includes('blue lid') || t.includes('paper & cardboard (blue') || t.includes('paper/cardboard (blue') || t.includes('paper & cardboard (yellow lid') || (t.includes('container recycling') && t.includes('paper & cardboard'));
                // Recycling - always show if mentioned
                if (t.includes('recycling')) items.push({ name: hasSeparatePC ? 'Containers' : 'Recycling', lid: 'yellow', color: 'bg-yellow-100 text-yellow-800 border-yellow-200' });
                // P&C only if separate stream
                if (hasSeparatePC) items.push({ name: 'P&C', lid: 'blue', color: 'bg-blue-100 text-blue-700 border-blue-200' });
                // FOGO/Organics
                if (t.includes('fogo') || t.includes('organics') || t.includes('garden')) items.push({ name: 'FOGO', lid: 'green', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' });
                return items;
              };

              // Parse generation rate type
              const parseRateType = (text) => {
                const t = text.toLowerCase();
                if (t.includes('per bedroom') || t.includes('1-bed') || t.includes('studio/1-bed') || t.includes('1 bed') || t.includes('studio')) return 'bedroom';
                if (t.includes('per unit') || t.includes('per dwelling') || t.includes('/unit/') || t.includes('dwellings per')) return 'unit';
                if (t.includes('calculator') || t.includes('table')) return 'calculator';
                return 'wmp';
              };

              // Parse chute allowance
              const parseChuteType = (text) => {
                const t = text.toLowerCase();
                const cs = (council.chute_streams || '').toLowerCase();
                if (t.includes('n/a') || t.includes('not applicable') || t.includes('not required') || t.includes('not explicitly') || t.includes('no explicit chute') || t.includes('not mentioned')) return { type: 'none', label: 'N/A or not specified' };
                if (t.includes('prohibited') && t.includes('fogo')) {
                  if (cs.includes('recycling') && cs.includes('residual')) return { type: 'gw+recy', label: 'GW + Recycling (no FOGO)' };
                }
                if (t.includes('dual chute') || t.includes('dual') || cs.includes('recycling') || cs.includes('and recycling') || t.includes('separate chutes')) return { type: 'gw+recy', label: 'GW + Recycling' };
                if (t.includes('permitted') || t.includes('required') || t.includes('chute')) return { type: 'gw', label: 'GW only' };
                return { type: 'unknown', label: 'Per WMP' };
              };

              const freqItems = parseFreq(council.collection_frequency || '', streams);
              const streamItems = parseStreams(streams);
              const rateType = parseRateType(rates);
              const chuteInfo = parseChuteType(chute);

              return (
                <div
                  key={council.council_name}
                  className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition-shadow"
                  data-testid={`council-card-${council.council_name.replace(/\s/g, '-').toLowerCase()}`}
                >
                  {/* Header */}
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="font-semibold text-gray-900 text-sm leading-tight pr-2">
                      {council.council_name}
                    </h3>
                    {council.notes?.includes('NEEDS VERIFICATION') ? (
                      <span className="shrink-0 text-xs px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full font-medium">Verify</span>
                    ) : council.has_data ? (
                      <span className="shrink-0 text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded-full font-medium">Data</span>
                    ) : (
                      <span className="shrink-0 text-xs px-2 py-0.5 bg-gray-100 text-gray-500 rounded-full font-medium">TBC</span>
                    )}
                  </div>

                  {council.has_data && (
                    <div className="space-y-3 mb-3">
                      {/* 1. WASTE STREAMS */}
                      <div data-testid={`streams-${council.council_name.replace(/\s/g, '-').toLowerCase()}`}>
                        <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">Waste Streams</p>
                        <div className="flex flex-wrap gap-1">
                          {streamItems.map((s, i) => (
                            <span key={i} className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded border ${s.color}`}>
                              <span className={`w-2 h-2 rounded-full ${s.lid === 'red' ? 'bg-red-500' : s.lid === 'yellow' ? 'bg-yellow-500' : s.lid === 'blue' ? 'bg-blue-500' : 'bg-green-500'}`} />
                              {s.name}
                            </span>
                          ))}
                        </div>
                      </div>

                      {/* 2. COLLECTION FREQUENCY */}
                      <div data-testid={`freq-${council.council_name.replace(/\s/g, '-').toLowerCase()}`}>
                        <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">Collection Frequency</p>
                        {freqItems.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {freqItems.map((f, i) => (
                              <span key={i} className={`inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded ${f.color}`}>
                                {f.stream}: {f.freq === 'W' ? 'Weekly' : f.freq === 'F' ? 'Fortnightly' : f.freq}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-[10px] text-gray-400 italic">Per schedule / WMP</span>
                        )}
                      </div>

                      {/* 3. GENERATION RATES */}
                      <div data-testid={`rates-${council.council_name.replace(/\s/g, '-').toLowerCase()}`}>
                        <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">Generation Rates (L/unit/wk)</p>
                        {(() => {
                          const extracted = council.residential_generation_rates_extracted || {};
                          const gw = extracted.garbage;
                          const rec = extracted.recycling || extracted.recycling_containers;
                          const org = extracted.organics;
                          const rType = council.generation_rate_type;
                          const hasNumeric = gw || rec || org;
                          return (
                            <>
                              {hasNumeric ? (
                                <div className="flex flex-wrap gap-1">
                                  {gw && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-red-50 text-red-700">GW: {gw}L</span>}
                                  {rec && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-yellow-50 text-yellow-700">Recy: {rec}L</span>}
                                  {org && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-green-50 text-green-700">Org: {org}L</span>}
                                  {rType && <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${rType === 'explicit' ? 'bg-cyan-50 text-cyan-600' : rType === 'derived_from_bin' ? 'bg-indigo-50 text-indigo-600' : rType === 'formula' ? 'bg-violet-50 text-violet-600' : 'bg-gray-50 text-gray-500'}`}>
                                    {rType === 'explicit' ? 'DCP' : rType === 'derived_from_bin' ? 'Derived' : rType === 'formula' ? 'Formula' : rType === 'bin_allocation' ? 'Bin-based' : 'WMP'}
                                  </span>}
                                </div>
                              ) : (
                                <span className={`inline-flex items-center text-[10px] font-bold px-1.5 py-0.5 rounded ${
                                  rType === 'no_data' ? 'bg-gray-100 text-gray-500' : 'bg-amber-50 text-amber-600'
                                }`}>
                                  {rType === 'bin_allocation' ? 'Bin allocation only' : rType === 'formula' ? 'Formula-based' : rType === 'no_data' ? 'No rate data' : 'Per WMP'}
                                </span>
                              )}
                              {council.dcp_url && (
                                <a href={council.dcp_url} target="_blank" rel="noopener noreferrer" className="text-[9px] text-blue-500 hover:text-blue-700 mt-0.5 block truncate">
                                  DCP Reference {council.dcp_page || ''}
                                </a>
                              )}
                            </>
                          );
                        })()}
                      </div>

                      {/* 4. CHUTE ALLOWANCES */}
                      <div data-testid={`chute-${council.council_name.replace(/\s/g, '-').toLowerCase()}`}>
                        <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">Chute Allowances</p>
                        <span className={`inline-flex items-center text-[10px] font-bold px-1.5 py-0.5 rounded ${
                          chuteInfo.type === 'gw+recy' ? 'bg-teal-100 text-teal-800' :
                          chuteInfo.type === 'gw' ? 'bg-orange-100 text-orange-800' :
                          chuteInfo.type === 'none' ? 'bg-gray-100 text-gray-500' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          {chuteInfo.label}
                        </span>
                        {council.chute_height_threshold && council.chute_height_threshold !== 'TBC' && !council.chute_height_threshold.toLowerCase().includes('n/a') && (
                          <span className="ml-1 text-[10px] text-gray-400">{council.chute_height_threshold}</span>
                        )}
                      </div>

                      {/* 5. COMMERCIAL RATES */}
                      {council.commercial_waste_rates && Object.keys(council.commercial_waste_rates).length > 0 && (
                        <div>
                          <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">Commercial Rates</p>
                          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-purple-50 text-purple-700">
                            {Object.keys(council.commercial_waste_rates).length} premises types
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex gap-2 pt-2 border-t border-gray-100">
                    <button
                      onClick={() => handleEditCouncil(council)}
                      className="flex-1 text-xs font-medium px-3 py-1.5 bg-amber-50 text-amber-700 rounded-lg hover:bg-amber-100 transition-colors"
                      data-testid={`edit-council-${council.council_name.replace(/\s/g, '-').toLowerCase()}`}
                    >
                      {council.has_data ? 'Edit' : 'Add Data'}
                    </button>
                    <button
                      onClick={() => handleDownloadPDF(council.council_name)}
                      className="flex-1 text-xs font-medium px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors"
                      data-testid={`pdf-council-${council.council_name.replace(/\s/g, '-').toLowerCase()}`}
                    >
                      PDF
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Table View */}
        {viewMode === 'table' && (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid="councils-table">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 text-left">
                    <th className="px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Council</th>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Waste Streams</th>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Collection Frequency</th>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Gen. Rates</th>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Chute Allowances</th>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredCouncils.map((council) => {
                    const streams = council.residential_waste_streams || '';
                    const freq = council.collection_frequency || '';
                    const rates = council.generation_rates_basis || '';
                    const chute = council.chute_allowances || '';
                    const fl = freq.toLowerCase();
                    const rl = rates.toLowerCase();
                    const cl = chute.toLowerCase();
                    const cs = (council.chute_streams || '').toLowerCase();

                    // Parse streams
                    const sl = streams.toLowerCase();
                    const streamBadges = [];
                    if (sl.includes('general waste') || sl.includes('residual')) streamBadges.push({ name: 'GW', color: 'bg-red-100 text-red-700', dot: 'bg-red-500' });
                    const hasSepPC = sl.includes('blue lid') || sl.includes('paper & cardboard (blue') || sl.includes('paper/cardboard (blue') || sl.includes('paper & cardboard (yellow lid') || (sl.includes('container recycling') && sl.includes('paper & cardboard'));
                    if (sl.includes('recycling')) streamBadges.push({ name: hasSepPC ? 'Containers' : 'Recycling', color: 'bg-yellow-100 text-yellow-800', dot: 'bg-yellow-500' });
                    if (hasSepPC) streamBadges.push({ name: 'P&C', color: 'bg-blue-100 text-blue-700', dot: 'bg-blue-500' });
                    if (sl.includes('fogo') || sl.includes('organics') || sl.includes('garden')) streamBadges.push({ name: 'FOGO', color: 'bg-emerald-100 text-emerald-700', dot: 'bg-green-500' });

                    // Parse freq - split by ; to avoid cross-segment matching
                    const freqBadges = [];
                    const segments = fl.split(';').map(s => s.trim());
                    const gwSeg = segments.find(s => s.includes('general waste') || s.includes(' gw') || s.includes('residual') || s.includes('garbage'));
                    if (gwSeg) {
                      if (gwSeg.includes('twice') || gwSeg.includes('2x')) freqBadges.push({ s: 'GW', f: '2x/wk', c: 'bg-purple-100 text-purple-800' });
                      else if (gwSeg.includes('fortnightly')) freqBadges.push({ s: 'GW', f: 'F', c: 'bg-amber-100 text-amber-800' });
                      else if (gwSeg.includes('weekly')) freqBadges.push({ s: 'GW', f: 'W', c: 'bg-green-100 text-green-800' });
                    }
                    const recySeg = segments.find(s => s.includes('recycling') || s.includes('commingled') || s.includes('container recycl'));
                    if (recySeg) {
                      if (recySeg.includes('fortnightly')) freqBadges.push({ s: hasSepPC ? 'Cont.' : 'Recy', f: 'F', c: 'bg-amber-100 text-amber-800' });
                      else if (recySeg.includes('weekly')) freqBadges.push({ s: hasSepPC ? 'Cont.' : 'Recy', f: 'W', c: 'bg-green-100 text-green-800' });
                    }
                    const fogoSeg = segments.find(s => s.includes('fogo') || s.includes('organics') || s.includes('garden') || s.includes('vegetation'));
                    if (fogoSeg) {
                      const fi = fogoSeg.indexOf('fortnightly');
                      const wi = fogoSeg.indexOf('weekly');
                      const ti = fogoSeg.indexOf('twice');
                      const positions = [
                        fi >= 0 ? { pos: fi, f: 'F', c: 'bg-amber-100 text-amber-800' } : null,
                        ti >= 0 ? { pos: ti, f: '2x/wk', c: 'bg-purple-100 text-purple-800' } : null,
                        wi >= 0 && (ti < 0 || wi < ti) ? { pos: wi, f: 'W', c: 'bg-green-100 text-green-800' } : null,
                      ].filter(Boolean);
                      if (positions.length > 0) {
                        const first = positions.sort((a, b) => a.pos - b.pos)[0];
                        freqBadges.push({ s: 'FOGO', f: first.f, c: first.c });
                      }
                    }
                    if (hasSepPC) {
                      const pcSeg = segments.find(s => s.includes('paper') || s.includes('p&c') || s.includes('cardboard'));
                      if (pcSeg) {
                        if (pcSeg.includes('fortnightly')) freqBadges.push({ s: 'P&C', f: 'F', c: 'bg-amber-100 text-amber-800' });
                        else if (pcSeg.includes('weekly')) freqBadges.push({ s: 'P&C', f: 'W', c: 'bg-green-100 text-green-800' });
                      }
                    }

                    // Rate type
                    const rateType = rl.includes('per bedroom') || rl.includes('1-bed') || rl.includes('studio/1-bed') || rl.includes('1 bed') || rl.includes('studio') ? 'bedroom' :
                      rl.includes('per unit') || rl.includes('per dwelling') || rl.includes('/unit/') || rl.includes('dwellings per') ? 'unit' :
                      rl.includes('calculator') || rl.includes('table') ? 'calc' : 'wmp';

                    // Chute type
                    const chuteType = cl.includes('n/a') || cl.includes('not applicable') || cl.includes('not required') || cl.includes('not explicitly') || cl.includes('no explicit') || cl.includes('not mentioned') ? 'none' :
                      cl.includes('dual chute') || cl.includes('dual') || cs.includes('recycling') || cs.includes('and recycling') || cl.includes('separate chutes') ? 'gw+recy' :
                      cl.includes('permitted') || cl.includes('required') || cl.includes('chute') ? 'gw' : 'wmp';

                    return (
                      <tr key={council.council_name} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <span className="text-sm font-medium text-gray-900">{council.council_name}</span>
                          {council.notes?.includes('NEEDS VERIFICATION') && (
                            <span className="ml-1.5 text-[9px] px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded font-medium align-middle">Verify</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-1">
                            {streamBadges.map((s, i) => (
                              <span key={i} className={`inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded ${s.color}`}>
                                <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
                                {s.name}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-1">
                            {freqBadges.length > 0 ? freqBadges.map((f, i) => (
                              <span key={i} className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${f.c}`}>
                                {f.s}: {f.f === 'W' ? 'Weekly' : f.f === 'F' ? 'Ftn' : f.f}
                              </span>
                            )) : (
                              <span className="text-[10px] text-gray-400 italic">Per schedule</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                            rateType === 'bedroom' ? 'bg-indigo-100 text-indigo-800' :
                            rateType === 'unit' ? 'bg-cyan-100 text-cyan-800' :
                            rateType === 'calc' ? 'bg-violet-100 text-violet-800' :
                            'bg-gray-100 text-gray-600'
                          }`}>
                            {rateType === 'bedroom' ? 'Per Bedroom' : rateType === 'unit' ? 'Per Unit' : rateType === 'calc' ? 'Calculator' : 'Per WMP'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                            chuteType === 'gw+recy' ? 'bg-teal-100 text-teal-800' :
                            chuteType === 'gw' ? 'bg-orange-100 text-orange-800' :
                            chuteType === 'none' ? 'bg-gray-100 text-gray-500' :
                            'bg-gray-100 text-gray-600'
                          }`}>
                            {chuteType === 'gw+recy' ? 'GW + Recycling' : chuteType === 'gw' ? 'GW Only' : chuteType === 'none' ? 'N/A' : 'Per WMP'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={() => handleEditCouncil(council)}
                              className="text-xs font-medium px-2.5 py-1 bg-amber-50 text-amber-700 rounded hover:bg-amber-100"
                            >
                              {council.has_data ? 'Edit' : 'Add'}
                            </button>
                            <button
                              onClick={() => handleDownloadPDF(council.council_name)}
                              className="text-xs font-medium px-2.5 py-1 bg-blue-50 text-blue-700 rounded hover:bg-blue-100"
                            >
                              PDF
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {filteredCouncils.length === 0 && (
          <div className="text-center py-12 text-gray-500" data-testid="no-results">
            No councils match your search.
          </div>
        )}
      </main>

      {/* Edit Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black/50 flex items-start justify-center pt-8 z-50 overflow-y-auto" data-testid="edit-council-modal">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4 mb-8">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <div>
                <h2 className="text-lg font-bold text-gray-900" data-testid="modal-council-name">
                  {editData.council_name}
                </h2>
                <p className="text-sm text-gray-500">DCP Waste Management Parameters</p>
              </div>
              <button
                onClick={() => setShowEditModal(false)}
                className="text-gray-400 hover:text-gray-600"
                data-testid="close-modal-btn"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleSaveCouncil}>
              <div className="px-6 py-4 max-h-[70vh] overflow-y-auto space-y-4">
                {error && (
                  <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                    {error}
                  </div>
                )}

                {/* Group parameters by category */}
                {(() => {
                  const groups = {};
                  PARAMETER_LABELS.forEach((p, idx) => {
                    if (!groups[p.group]) groups[p.group] = [];
                    groups[p.group].push({ ...p, idx });
                  });

                  return Object.entries(groups).map(([groupName, params]) => (
                    <div key={groupName} className="space-y-3">
                      <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-100 pb-1">
                        {groupName}
                      </h3>
                      {params.map((param) => (
                        <div key={param.key}>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {param.idx + 1}. {param.label}
                          </label>
                          <textarea
                            value={editData[param.key] || ''}
                            onChange={(e) => setEditData(prev => ({ ...prev, [param.key]: e.target.value }))}
                            rows={2}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 resize-none"
                            placeholder="TBC"
                            data-testid={`input-${param.key}`}
                          />
                        </div>
                      ))}
                    </div>
                  ));
                })()}

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <textarea
                    value={editData.notes || ''}
                    onChange={(e) => setEditData(prev => ({ ...prev, notes: e.target.value }))}
                    rows={3}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                    placeholder="Additional notes about this council..."
                    data-testid="input-notes"
                  />
                </div>
              </div>

              {/* Modal Footer */}
              <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
                  data-testid="cancel-edit-btn"
                >
                  Cancel
                </button>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => handleDownloadPDF(editData.council_name)}
                    className="px-4 py-2 text-sm font-medium bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100"
                    data-testid="modal-pdf-btn"
                  >
                    Download PDF
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="px-5 py-2 text-sm font-medium bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50"
                    data-testid="save-council-btn"
                  >
                    {saving ? 'Saving...' : 'Save Parameters'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminNSWCouncils;
